import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faChevronDown, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useLayout } from "@/contexts/LayoutContext";
import { fetchSelections, deleteSelection } from "@/api/selection/api";
import { Selections } from "@/types/domain";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/shadcn/dialog";
import { Button } from "@/components/shadcn/button";
import { LoadingOverlay } from "@/components/common/Loading/LoadingOverlay";

export const SelectionsDropdown = () => {
  const { data } = useLayout();
  const routes = data.appRoutes;
  const [selections, setSelections] = useState<Selections | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ value: string; text: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (open && !selections && !fetchFailed) {
      fetchSelections().then(setSelections).catch(() => { setFetchFailed(true); });
    }
  };

  const selectionUrl = (selectionId: string) =>
    `${routes.dashboard}?selectionId=${selectionId}&target=Sales.aspx`;

  const handleDeleteClick = (selectionId: string, selectionText: string) => {
    // No stopPropagation — let DropdownMenu close naturally so it fully cleans up its
    // Radix scroll lock before the Dialog opens and adds its own. setTimeout defers the
    // Dialog open until the next event-loop tick, after Radix has finished cleanup.
    setTimeout(() => setPendingDelete({ value: selectionId, text: selectionText }), 0);
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    const id = pendingDelete.value;
    setPendingDelete(null);
    setDeleting(true);
    deleteSelection(id)
      .then(() => setSelections(prev => prev && {
        globalSelections: prev.globalSelections.filter(s => s.value !== id),
        personalSelections: prev.personalSelections.filter(s => s.value !== id),
      }))
      .finally(() => setDeleting(false));
  };

  const globalSelections = selections?.globalSelections ?? [];
  const personalSelections = selections?.personalSelections ?? [];

  return (
    <>
      <LoadingOverlay isVisible={deleting} message="Deleting selection..." />
      {/* Dialog must be a sibling of DropdownMenu, never nested inside it.
          Nesting causes the dialog state to be destroyed when DropdownMenuSubContent
          unmounts on close, leaving a stale Radix scroll-lock increment on the body. */}
      <Dialog open={pendingDelete !== null} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <DialogContent className="sm:max-w-100">
          <DialogHeader>
            <DialogTitle>Delete Selection</DialogTitle>
          </DialogHeader>
          <div className="text-muted-foreground mt-2">
            {pendingDelete && <>Are you sure you want to delete <strong>{pendingDelete.text}</strong>?</>}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">Cancel</Button>
            </DialogClose>
            <Button variant="destructive" type="button" onClick={handleConfirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <DropdownMenu onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-1 px-3 h-8 text-sm text-white bg-(--layout-brand) hover:bg-(--layout-brand-hover) border border-white/40 hover:border-white rounded-[6px] transition-colors focus:outline-none">
            <FontAwesomeIcon icon={faFilter} size="sm" />
            <FontAwesomeIcon icon={faChevronDown} size="xs" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end" className="min-w-50">
          <DropdownMenuLabel>Existing Selections</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {/* Global selections */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Global</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
              {fetchFailed ? (
                <DropdownMenuItem disabled>Failed to load</DropdownMenuItem>
              ) : !selections ? (
                <DropdownMenuItem disabled>Loading…</DropdownMenuItem>
              ) : globalSelections.length === 0 ? (
                <DropdownMenuItem disabled>No selections</DropdownMenuItem>
              ) : (
                globalSelections.map(s => (
                  <DropdownMenuItem key={s.value} asChild>
                    <a href={selectionUrl(s.value)}>{s.text}</a>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {/* Personal selections */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Personal</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
              {fetchFailed ? (
                <DropdownMenuItem disabled>Failed to load</DropdownMenuItem>
              ) : !selections ? (
                <DropdownMenuItem disabled>Loading…</DropdownMenuItem>
              ) : personalSelections.length === 0 ? (
                <DropdownMenuItem disabled>No selections</DropdownMenuItem>
              ) : (
                personalSelections.map(s => (
                  <DropdownMenuItem key={s.value} asChild>
                    <div className="flex items-center justify-between gap-2 w-full px-2 py-1">
                      <a href={selectionUrl(s.value)} className="flex-1 truncate">{s.text}</a>
                      <button
                        onClick={() => handleDeleteClick(s.value, s.text)}
                        className="text-muted-foreground hover:text-destructive shrink-0"
                        title="Delete selection"
                      >
                        <FontAwesomeIcon icon={faTrash} size="xs" />
                      </button>
                    </div>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}


